import React from 'react';
import Icon from '../../../components/AppIcon';

const ReferralLeaderboard = ({ leaderboard = [], currentUserId }) => {
  const getRankBadge = (rank) => {
    if (rank === 1) {
      return { icon: 'Trophy', color: 'var(--color-warning)', bgColor: 'bg-warning/10' };
    } else if (rank === 2) {
      return { icon: 'Medal', color: 'var(--color-muted-foreground)', bgColor: 'bg-muted' };
    } else if (rank === 3) {
      return { icon: 'Award', color: 'var(--color-accent)', bgColor: 'bg-accent/10' };
    }
    return { icon: 'User', color: 'var(--color-muted-foreground)', bgColor: 'bg-muted' };
  };

  if (leaderboard?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="flex items-center justify-center h-16 w-16 rounded-full bg-muted">
            <Icon name="Trophy" size={32} color="var(--color-muted-foreground)" />
          </div>
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No Leaderboard Data</h3>
        <p className="text-sm text-muted-foreground">
          Be the first to make referrals and climb the leaderboard!
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-muted/50 px-4 py-3 border-b border-border">
        <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          <div className="col-span-1">Rank</div>
          <div className="col-span-5">Member</div>
          <div className="col-span-2 text-center">Referrals</div>
          <div className="col-span-2 text-center">Success</div>
          <div className="col-span-2 text-right">Rewards</div>
        </div>
      </div>

      {/* Leaderboard Entries */}
      <div className="divide-y divide-border">
        {leaderboard?.map((entry) => {
          const rankBadge = getRankBadge(entry?.rank);
          const isCurrentUser = entry?.user_id === currentUserId;
          const successRate = entry?.total_referrals > 0
            ? Math.round((entry?.successful_referrals / entry?.total_referrals) * 100)
            : 0;

          return (
            <div
              key={entry?.id}
              className={`
                px-4 py-4 hover:bg-muted/30 transition-colors
                ${isCurrentUser ? 'bg-primary/5 border-l-4 border-l-primary' : ''}
              `}
            >
              <div className="grid grid-cols-12 gap-4 items-center">
                {/* Rank */}
                <div className="col-span-1">
                  <div className={`flex items-center justify-center h-10 w-10 rounded-full ${rankBadge?.bgColor}`}>
                    {entry?.rank <= 3 ? (
                      <Icon name={rankBadge?.icon} size={20} color={rankBadge?.color} />
                    ) : (
                      <span className="text-sm font-bold text-muted-foreground">{entry?.rank}</span>
                    )}
                  </div>
                </div>

                {/* Member Info */}
                <div className="col-span-5">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      {entry?.user_profiles?.avatar_url ? (
                        <img
                          src={entry?.user_profiles?.avatar_url}
                          alt={entry?.user_profiles?.full_name || 'Member'}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10">
                          <Icon name="User" size={20} color="var(--color-primary)" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground truncate">
                        {entry?.user_profiles?.full_name || 'Unknown Member'}
                        {isCurrentUser && (
                          <span className="ml-2 text-xs text-primary font-normal">(You)</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Total Referrals */}
                <div className="col-span-2 text-center">
                  <div className="flex flex-col">
                    <span className="text-lg font-bold text-foreground">{entry?.total_referrals}</span>
                    <span className="text-xs text-muted-foreground">total</span>
                  </div>
                </div>

                {/* Successful Referrals */}
                <div className="col-span-2 text-center">
                  <div className="flex flex-col">
                    <span className="text-lg font-bold text-success">{entry?.successful_referrals}</span>
                    <span className="text-xs text-muted-foreground">{successRate}%</span>
                  </div>
                </div>

                {/* Total Rewards */}
                <div className="col-span-2 text-right">
                  <div className="flex flex-col items-end">
                    <span className="text-lg font-bold text-accent">
                      ${parseFloat(entry?.total_rewards_earned || 0)?.toFixed(2)}
                    </span>
                    <span className="text-xs text-muted-foreground">earned</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReferralLeaderboard;